/* Copyright (c) 2025 Xperi. All Rights Reserved. */
import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { VodItemKernelType, VodItemType } from 'client-code/vod-browse';

import { Constants } from '../../core/constants';
import { ContentType, InteractiveComponents } from '../../core/enums';
import { TransitionScreenType } from '../../core/enums/TransitionScreenType';
import { IInfoCard, InfoCardManager } from '../../core/infocard';
import { Log } from '../../core/logging';
import { ScreenTaskControllerTypes } from '../../core/navigation';
import { ScreenController } from '../../core/navigation/ScreenController';
import { TScreen } from '../../core/navigation/types';
import { NetworkManager } from '../../core/network/NetworkManager';
import { TGalleryScreenState, TVodPlayActionStateType } from '../../core/types';
import { GalleryScreenComponent, MetadataOperations } from '../../core/usecases';
import { CcbSwitch } from '../../core/utils/ccb';
import { CcbSwitchName } from '../../core/utils/ccb/enums';
import { GridTypes } from '../../core/utils/GridTypes';
import { setGridItemFocusAction, setGridItemsAction } from '../grid/grid.actions';
import { onKeyDownGrid, onKeyLeftGrid, onKeyRightGrid, onKeyUpGrid } from '../grid/grid.reducers';
import {
    clearActiveInteractiveComponent,
    setActiveInteractiveComponent,
} from '../interactivelayer/interactiveLayer.reducers';
import {
    onKeyBackNoNetworkOverlayGalleryScreen,
    onKeyOkNoNetworkOverlayGalleryScreen,
    showErrorOverlayGalleryScreen,
    showNetworkOverlayGalleryScreen,
    showNetworkWhisperGalleryScreen,
} from '../overlay/galleryscreennonetworkoverlay/galleryscreennonetworkoverlay.reducers';
import {
    onKeyBackPcPinOverlay,
    onKeyDigitPcPinOverlay,
    onKeyDownPcPinOverlay,
    onKeyLeftPcPinOverlay,
    onKeyOkPcPinOverlay,
    onKeyRightPcPinOverlay,
    onKeyUpPcPinOverlay,
    onMouseClickPcPinOverlay,
    onMouseOutPcPinOverlay,
    onMouseOverPcPinOverlay,
} from '../overlay/pcpinoverlay/pcpinoverlay.reducers';
import {
    onKeyOkVodPlayErrorOverlay,
    onMouseClickVodPlayErrorOverlay,
} from '../overlay/vodplayerroroverlay/vodplayerroroverlay.reducers';
import {
    onKeyOkVodUpSellOverlay,
    onMouseClickVodUpSellOverlay,
} from '../overlay/vodupselloverlay/vodupselloverlay.reducers';
import {
    hideOverlayDialog,
    onKeyDownOverlayDialog,
    onKeyUpOverlayDialog,
    onMouseOutOverlayDialogOperations,
    onMouseOverOverlayDialogOperations,
} from '../overlaydialog/overlaydialog.reducers';
import {
    collectionScreenTransition,
    galleryScreenTransition,
    programScreenTransition,
} from '../screens/navigation/screensnavigation';
import { showPreviousScreen, showVodBrowseMainScreen } from '../screens/screens.reducers';
import { notifyVodPlayActionState } from '../vodplayaction/vodplayaction.reducers';
import { showWhisper } from '../whisper/whisper.reducer';
import {
    setGalleryGhostModeAction,
    updateGalleryCaretVisibilityAction,
} from './galleryscreen.actions';

const calculatedGridPosition = (value: string): { row: number; column: number } | null => {
    if (!value || !value.startsWith('grid_item_')) return null;
    const positions = value.split('_');
    const row = Number(positions[2]);
    const column = Number(positions[3]);
    return { row, column };
};

export const defaultHandelingForOnKeyOK = () => (dispatch: Function, getState: Function) => {
    if (NetworkManager.isNetworkDisconnected()) {
        dispatch(showNetworkOverlayGalleryScreen());
        return;
    }
    const { focusedItem, ghostMode, items, columnLimit } = getState().grid[GridTypes.GALLERY] || {};

    if (ghostMode) return;

    if (focusedItem && items.length > 0) {
        const highlightedItemIndex = focusedItem.row * columnLimit + focusedItem.column;
        const item = items[highlightedItemIndex];
        Log.d(
            'GalleryScreenReducer',
            'onKeyOKGallery -- highlightedItemIndex:',
            highlightedItemIndex,
        );
        Diagnostics.debug(
            'GalleryScreenReducer',
            'onKeyOKGallery -- highlightedItemIndex:',
            highlightedItemIndex,
        );

        switch (item.vodItemType.toLowerCase()) {
            case VodItemType.EpisodeVodItem.toLowerCase():
            case VodItemType.MovieVodItem.toLowerCase():
            case VodItemType.SpecialVodItem.toLowerCase():
                {
                    const currScreen = ScreenController.getInstance().getScreen<TScreen>();
                    const currScreenProperties = currScreen.getProperties();
                    const focusedItemValue = getState().grid[GridTypes.GALLERY]?.focusedItem || {
                        row: 0,
                        column: 0,
                    };
                    const { row, column } = focusedItemValue;
                    currScreen.setProperties({
                        ...currScreenProperties,
                        row,
                        column,
                    } as TGalleryScreenState & TScreen);
                    dispatch(handleProgramScreenTransition(item));
                }
                break;
            case VodItemType.FolderVodItem.toLowerCase():
                dispatch(
                    galleryScreenTransition(
                        item.vodItemKernel.isAdult,
                        item.vodItemKernel.vodItemId,
                        item.vodItemKernel.partnerId,
                        item.vodItemKernel.screenType,
                        item.title || '',
                    ),
                );
                break;
            case VodItemType.UnifiedCollectionVodItem.toLowerCase():
                dispatch(
                    collectionScreenTransition(
                        {
                            unifiedCollectionId: item.vodItemKernel.unifiedCollectionId,
                            transitionScreen: TransitionScreenType.GALLERY_SCREEN,
                            partnerId: item.vodItemKernel.partnerId,
                            contentSupplierPartnerId: item.vodItemKernel.contentSupplierPartnerId,
                            collapsedVodItemId: item.vodItemKernel.collapsedVodItemId,
                        },
                        showWhisper,
                    ),
                );
                break;
            default:
        }
    }
};

const handleProgramScreenTransition = (item: any) => (dispatch: any) => {
    if (
        item.vodItemKernel.vodItemKernelType.toLowerCase() ===
        VodItemKernelType.OfferVodItemKernel.toLowerCase()
    ) {
        Log.d('GalleryScreenReducer', 'handleProgramScreenTransition - OfferVodItemKernel');
        Diagnostics.debug(
            'GalleryScreenReducer',
            'handleProgramScreenTransition - OfferVodItemKernel',
        );
        dispatch(
            programScreenTransition(
                {
                    id: item.vodItemKernel.offerId,
                    contentType: ContentType.VOD_OFFER_ID,
                    partnerId: item.vodItemKernel.partnerId,
                    contentSupplierPartnerId: item.vodItemKernel.contentSupplierPartnerId,
                },
                showWhisper,
            ),
        );
    } else {
        Log.d('GalleryScreenReducer', 'handleProgramScreenTransition - ContentVodItemKernel');
        Diagnostics.debug(
            'GalleryScreenReducer',
            'handleProgramScreenTransition - ContentVodItemKernel',
        );
        dispatch(
            programScreenTransition(
                {
                    id: item.vodItemKernel.contentId,
                    contentType: ContentType.CONTENT_ID,
                    partnerId: item.vodItemKernel.partnerId,
                    contentSupplierPartnerId: item.vodItemKernel.contentSupplierPartnerId,
                },
                showWhisper,
            ),
        );
    }
};

export const onKeyInfoGalleryScreen = () => (dispatch: Function, getState: Function) => {
    if (
        CcbSwitch.getInstance().isEnabledAndOverriden(
            CcbSwitchName.ENABLE_INFO_CARD_REMOVE_IN_KST_12193,
        )
    ) {
        const { focusedItem, items, rowLimit } = getState().grid[GridTypes.GALLERY] || {};
        const metadataOperations = new MetadataOperations();
        try {
            const { row, column } = focusedItem;
            const highlightedItemIndex = row * rowLimit + column;
            const highlightedItem = items[highlightedItemIndex];
            const infoCardKernel = metadataOperations.getInfoCardKernelByVodItem(highlightedItem);
            const infoCard: IInfoCard = InfoCardManager.getInstance();
            infoCard.setInfoCardKernel(infoCardKernel);
            infoCard.show();
        } catch (error) {
            Log.e('GalleryScreenReducer', 'onKeyInfoGalleryScreen Error:', error);
            Diagnostics.error('GalleryScreenReducer', 'onKeyInfoGalleryScreen Error:', error);
        }
    }
};

export const handleOnKeyBackForDefaultGalleryScreen =
    () => (dispatch: Function, getState: Function) => {
        const { isOverlayDialogActive } = getState().overlayDialog;
        if (NetworkManager.isNetworkDisconnected() && !isOverlayDialogActive) {
            dispatch(showNetworkWhisperGalleryScreen());
            return;
        }
        dispatch(showPreviousScreen());
    };

export const handleOnKeyBackForNoNetworkOverlayGalleryScreen =
    () => (dispatch: Function, getState: Function) => {
        const { isOverlayDialogActive } = getState().overlayDialog;
        if (NetworkManager.isNetworkDisconnected() && !isOverlayDialogActive) {
            dispatch(showNetworkWhisperGalleryScreen());
        }
        dispatch(onKeyBackNoNetworkOverlayGalleryScreen());
    };

export const onKeyBackGallery = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyBackPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_GALLERY_SCREEN_TASKS:
            dispatch(handleOnKeyBackForDefaultGalleryScreen());
            break;
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY_TASKS_GALLERY_SCREEN:
            dispatch(handleOnKeyBackForNoNetworkOverlayGalleryScreen());
            break;
        case ScreenTaskControllerTypes.ERROR_OVERLAY_TASKS_GALLERY_SCREEN:
            if (NetworkManager.isNetworkDisconnected()) dispatch(showVodBrowseMainScreen());
            else dispatch(showPreviousScreen());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(hideOverlayDialog());
            break;
        default:
    }
};

export const onKeyDownGallery = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDownPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_GALLERY_SCREEN_TASKS:
            dispatch(onKeyDownGrid(GridTypes.GALLERY));
            break;
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY_TASKS_GALLERY_SCREEN:
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onKeyDownOverlayDialog());
            break;
        default:
    }
};

export const onKeyDigitGalleryView = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDigitPcPinOverlay());
            break;
        default:
    }
};

export const onKeyLeftGallery = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onKeyLeftPcPinOverlay());
    } else if (screenTaskController === ScreenTaskControllerTypes.DEFAULT_GALLERY_SCREEN_TASKS) {
        const grid = getState().grid[GridTypes.GALLERY];
        if (!grid) return;

        const { focusedItem } = grid;
        const currentPage = GalleryScreenComponent.getCurrentPage();

        // If we're at the leftmost column (column 0) and not on the first page
        if (focusedItem.column === 0 && focusedItem.row === 0 && currentPage > 0) {
            // Navigate to previous page and focus on the last element
            dispatch(setPreviousPageInGalleryScreen(true)); // Focus on last element
        } else {
            // Normal left navigation within the current page
            dispatch(onKeyLeftGrid(GridTypes.GALLERY));
        }
    }
};

export const onKeyPlayGallery = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { focusedItem, items, columnLimit } = getState().grid[GridTypes.GALLERY] || {};
    const highlightedItemIndex = focusedItem.row * columnLimit + focusedItem.column;
    const item = items[highlightedItemIndex];

    if (screenTaskController === ScreenTaskControllerTypes.DEFAULT_GALLERY_SCREEN_TASKS) {
        const kernelType = item.vodItemKernel.vodItemKernelType.toLowerCase();
        const params = {
            id: '',
            partnerId: item.vodItemKernel.partnerId,
            collapsedVodItemId: item.vodItemKernel.collapsedVodItemId,
        };
        let itemType;
        switch (kernelType) {
            case VodItemKernelType.OfferVodItemKernel.toLowerCase():
                itemType = 'offer';
                params.id = item.vodItemKernel.offerId;
                break;
            case VodItemKernelType.ContentVodItemKernel.toLowerCase():
                itemType = 'content';
                params.id = item.vodItemKernel.contentId;
                break;
            case VodItemKernelType.UnifiedCollectionVodItemKernel.toLowerCase():
                itemType = 'unifiedCollection';
                params.id = item.vodItemKernel.unifiedCollectionId;
                break;
            default:
                itemType = undefined;
        }
        if (itemType) {
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.INIT,
                    vodRequiredParams: {
                        id: params.id,
                        partnerId: params.partnerId,
                        itemType,
                        ...(itemType !== 'offer' && {
                            collapsedVodItemId: params.collapsedVodItemId,
                            contentSupplierPartnerId:
                                item.vodItemKernel.contentSupplierPartnerId || '',
                        }),
                    },
                }),
            );
        }
    }
};

export const onKeyRightGallery = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onKeyRightPcPinOverlay());
    } else if (screenTaskController === ScreenTaskControllerTypes.DEFAULT_GALLERY_SCREEN_TASKS) {
        const grid = getState().grid[GridTypes.GALLERY];
        if (!grid) return;

        const { focusedItem, columnLimit, rowLimit } = grid;
        const currentPage = GalleryScreenComponent.getCurrentPage();
        const totalItems = GalleryScreenComponent.getTotalItems();
        const galleryScreen = new GalleryScreenComponent();
        const maxItemsInPage = galleryScreen.getMaxItemsInPage();
        const totalPages = Math.ceil(totalItems / maxItemsInPage);

        // If we're at the rightmost column and not on the last page
        if (
            focusedItem.column === columnLimit - 1 &&
            focusedItem.row === rowLimit - 1 &&
            currentPage < totalPages - 1
        ) {
            // Navigate to next page and focus on the first element (top-left)
            dispatch(setNextPageInGalleryScreen(true)); // Focus on first element
        } else {
            // Normal right navigation within the current page
            dispatch(onKeyRightGrid(GridTypes.GALLERY));
        }
    }
};

export const onKeyUpGallery = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyUpPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_GALLERY_SCREEN_TASKS:
            dispatch(onKeyUpGrid(GridTypes.GALLERY));
            break;
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY_TASKS_GALLERY_SCREEN:
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onKeyUpOverlayDialog());
            break;
        default:
    }
};

export const onMouseOverGallery = () => (dispatch: Function, getState: Function) => {
    const { inputFactory, grid, screens } = getState();
    const { value } = inputFactory;
    const { screenTaskController } = screens;
    const { ghostMode } = grid[GridTypes.GALLERY];
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOverPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_GALLERY_SCREEN_TASKS:
            if (value === InteractiveComponents.NORTH || value === InteractiveComponents.SOUTH) {
                dispatch(setActiveInteractiveComponent(value));
                dispatch(updateGalleryCaretVisibilityAction(true));
            }
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY_TASKS_GALLERY_SCREEN:
            dispatch(onMouseOverOverlayDialogOperations());
            break;
        default:
    }

    const position = calculatedGridPosition(value);

    if (!position) {
        dispatch(setGalleryGhostModeAction(GridTypes.GALLERY, true));
        return;
    }
    if (ghostMode) dispatch(setGalleryGhostModeAction(GridTypes.GALLERY, false));

    if (position) {
        dispatch(
            setGridItemFocusAction(GridTypes.GALLERY, {
                row: position.row,
                column: position.column,
            }),
        );
    }
};

export const onMouseOutGallery = () => (dispatch: Function, getState: Function) => {
    const { inputFactory, screens } = getState();
    const { value } = inputFactory;
    const { screenTaskController } = screens;
    dispatch(setGalleryGhostModeAction(GridTypes.GALLERY, true));
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOutPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_GALLERY_SCREEN_TASKS:
            if (value === InteractiveComponents.NORTH || value === InteractiveComponents.SOUTH) {
                dispatch(clearActiveInteractiveComponent());
                dispatch(updateGalleryCaretVisibilityAction(false));
            }
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY_TASKS_GALLERY_SCREEN:
            dispatch(onMouseOutOverlayDialogOperations());
            break;
        default:
    }
};

export const onMouseClickGallery = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseClickPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_GALLERY_SCREEN_TASKS:
            dispatch(onMouseClickGalleryInteractiveLayerOperations());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.FORMAT_SELECTOR_MOUSE_CLICK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.VOD_RESUME_OVERLAY_MOUSE_CLICK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
            dispatch(onMouseClickVodPlayErrorOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onMouseClickVodUpSellOverlay());
            break;
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY_TASKS_GALLERY_SCREEN:
            dispatch(onKeyOkNoNetworkOverlayGalleryScreen());
            break;
        default:
    }
};

export const onKeyOKGallery = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { isOverlayDialogActive } = getState().overlayDialog;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyOkPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_GALLERY_SCREEN_TASKS:
            dispatch(defaultHandelingForOnKeyOK());
            break;
        case ScreenTaskControllerTypes.NO_NETWORK_OVERLAY_TASKS_GALLERY_SCREEN:
            dispatch(onKeyOkNoNetworkOverlayGalleryScreen());
            break;
        case ScreenTaskControllerTypes.ERROR_OVERLAY_TASKS_GALLERY_SCREEN:
            if (isOverlayDialogActive) dispatch(hideOverlayDialog());
            dispatch(showPreviousScreen());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.FORMAT_SELECTOR_KEY_OK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.VOD_RESUME_OVERLAY_KEY_OK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
            dispatch(onKeyOkVodPlayErrorOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onKeyOkVodUpSellOverlay());
            break;
        default:
    }
};

export const gallerySetCaretInvisible = () => (dispatch: Function, getState: Function) => {
    const { isOverlayDialogActive } = getState().overlayDialog;
    if (isOverlayDialogActive) return;

    dispatch(clearActiveInteractiveComponent());
    dispatch(updateGalleryCaretVisibilityAction(false));
};

export const setNextPageInGalleryScreen =
    (focusOnFirstElement = false) =>
    async (dispatch: Function, getState: Function) => {
        const { grid } = getState();
        const galleryGridForNextPage = grid[GridTypes.GALLERY];
        if (!galleryGridForNextPage) return;

        const currentPage = GalleryScreenComponent.getCurrentPage();
        const totalItemsInGallery = GalleryScreenComponent.getTotalItems();
        const totalAvailableItems = GalleryScreenComponent.getItemsSeenSoFar(currentPage);

        if (totalAvailableItems >= totalItemsInGallery) {
            return;
        }

        dispatch(
            setGridItemsAction(GridTypes.GALLERY, {
                ...galleryGridForNextPage,
                loadingState: true,
            }),
        );

        const { focusedItem, ghostMode } = galleryGridForNextPage;
        const screenPropertiesForNextPage = ScreenController.getInstance()
            .getScreen<TGalleryScreenState>()
            .getProperties();
        const parentVodItemIdForNextPage = screenPropertiesForNextPage?.parentVodItemId;
        if (!parentVodItemIdForNextPage) {
            Diagnostics.error('GalleryScreenReducer', 'parentVodItemIdForNextPage is undefined');
            return;
        }
        const galleryScreenGridGenerator = new GalleryScreenComponent();
        const resultForNextPage = await galleryScreenGridGenerator.getNextGalleryPage(
            parentVodItemIdForNextPage,
            focusedItem,
        );

        if (currentPage + 1 !== GalleryScreenComponent.getCurrentPage()) return;

        if (resultForNextPage?.response?.errorCode) {
            dispatch(showErrorOverlayGalleryScreen(resultForNextPage?.response?.errorCode));
            return;
        }
        if (!resultForNextPage?.gridParams) {
            Diagnostics.error('GalleryScreenReducer', 'gridParams is undefined to shift focus');
            return;
        }

        const {
            rowLimit,
            colLimit,
            lastcolumn: lastColumnInCurrentPage,
        } = resultForNextPage?.gridParams;

        if (resultForNextPage?.response?.items) {
            // Determine focus position based on navigation type
            let finalRow;
            let finalColumn;

            if (focusOnFirstElement) {
                // For right arrow key navigation: focus on first element (top-left)
                finalRow = 0;
                finalColumn = 0;
            } else {
                // For channel down/key down navigation: maintain row position
                finalRow = Math.min(focusedItem.row, rowLimit - 1);
                finalColumn = lastColumnInCurrentPage;
            }

            dispatch(
                setGridItemsAction(GridTypes.GALLERY, {
                    isActive: true,
                    items: resultForNextPage?.response.items,
                    focusedItem: { row: finalRow, column: finalColumn },
                    rowLimit,
                    columnLimit: colLimit,
                    ghostMode,
                    loadingState: false,
                }),
            );
        }
    };

export const setPreviousPageInGalleryScreen =
    (focusOnLastElement = false) =>
    async (dispatch: Function, getState: Function) => {
        const { grid } = getState();
        const ForPreviousPage = grid[GridTypes.GALLERY];
        if (!ForPreviousPage) return;
        const currentPage = GalleryScreenComponent.getCurrentPage();

        if (currentPage === 0) return;

        dispatch(
            setGridItemsAction(GridTypes.GALLERY, {
                ...ForPreviousPage,
                loadingState: true,
            }),
        );

        const { focusedItem, ghostMode } = ForPreviousPage;
        const screenPropertiesForPreviousPage = ScreenController.getInstance()
            .getScreen<TGalleryScreenState>()
            .getProperties();

        const parentVodItemIdForPreviousPage = screenPropertiesForPreviousPage?.parentVodItemId;
        if (!parentVodItemIdForPreviousPage) {
            Diagnostics.error(
                'GalleryScreenReducer',
                'parentVodItemIdForPreviousPage is undefined',
            );
            return;
        }

        const galleryScreenGridGenerator = new GalleryScreenComponent();
        const resultForPreviousPage = await galleryScreenGridGenerator.getPrevGalleryPage(
            parentVodItemIdForPreviousPage,
            focusedItem,
        );

        if (currentPage - 1 !== GalleryScreenComponent.getCurrentPage()) return;

        if (resultForPreviousPage?.response?.errorCode) {
            dispatch(showErrorOverlayGalleryScreen(resultForPreviousPage?.response?.errorCode));
            return;
        }

        if (!resultForPreviousPage?.gridParams) {
            Diagnostics.error('GalleryScreenReducer', 'gridParams is undefined to shift focus.');
            return;
        }
        const {
            rowLimit,
            colLimit,
            lastcolumn: lastColumnInCurrentPage,
        } = resultForPreviousPage?.gridParams;
        if (resultForPreviousPage?.response?.items) {
            // Determine focus position based on navigation type
            let finalRow;
            let finalColumn;

            if (focusOnLastElement) {
                // For left arrow key navigation: focus on last element (bottom-right)
                finalRow = rowLimit - 1;
                finalColumn = lastColumnInCurrentPage;
            } else {
                // For channel up/key up navigation: maintain row position
                finalRow = Math.min(focusedItem.row, rowLimit - 1);
                finalColumn = lastColumnInCurrentPage;
            }

            dispatch(
                setGridItemsAction(GridTypes.GALLERY, {
                    isActive: true,
                    items: resultForPreviousPage?.response.items,
                    focusedItem: { row: finalRow, column: finalColumn },
                    rowLimit,
                    columnLimit: colLimit,
                    ghostMode,
                    loadingState: false,
                }),
            );
        }
    };

export const onMouseClickGalleryInteractiveLayerOperations =
    () => (dispatch: Function, getState: Function) => {
        const { activeComponent } = getState().interactiveLayer;
        const grid = getState().grid[GridTypes.GALLERY];
        if (!grid) return;

        const {
            inputFactory: { value },
        } = getState();
        if (value === Constants.VIDEO_PLAYER_UI_ID) return;

        const { items, columnLimit, focusedItem } = grid;
        const totalItems = Array.isArray(items) ? items.length : 0;
        const { row, column } = focusedItem;

        switch (activeComponent) {
            case InteractiveComponents.SOUTH:
                dispatch(setNextPageInGalleryScreen());
                break;
            case InteractiveComponents.NORTH:
                dispatch(setPreviousPageInGalleryScreen());
                break;
            default:
                break;
        }

        const targetIndex = row * columnLimit + column;
        if (
            targetIndex <= totalItems &&
            activeComponent !== InteractiveComponents.SOUTH &&
            activeComponent !== InteractiveComponents.NORTH
        ) {
            dispatch(onKeyOKGallery());
        }
    };

export const onKeyChannelUpGalleryScreen = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const grid = getState().grid[GridTypes.GALLERY];
    if (!grid) return;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_GALLERY_SCREEN_TASKS:
            dispatch(setPreviousPageInGalleryScreen());
            break;
        default:
    }
};

export const onKeyChannelDownGalleryScreen = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const grid = getState().grid[GridTypes.GALLERY];
    if (!grid) return;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_GALLERY_SCREEN_TASKS:
            dispatch(setNextPageInGalleryScreen());
            break;
        default:
    }
};
